import { useMemo } from 'react';
import MetricCard from '../shared/MetricCard';
import DataTable from '../shared/DataTable';
import EmptyState from '../shared/EmptyState';
import { formatMoney, formatPercent, formatDate } from '../../utils/formatters';

export default function VendorPriceTrends({ data }) {
  const items = data?.changes || [];
  const increaseCount = data?.increase_count || 0;
  const decreaseCount = data?.decrease_count || 0;
  const totalIncrease = data?.total_increase_usd || 0;

  const columns = useMemo(() => [
    {
      key: 'part_name',
      label: 'Part',
      render: (row) => (
        <div>
          <div className="font-bold text-sm">{row.part_name}</div>
          <div className="text-xs text-gray-400">{row.part_number}</div>
        </div>
      ),
    },
    {
      key: 'vendor_name',
      label: 'Vendor',
      render: (row) => <span className="text-sm">{row.vendor_name}</span>,
    },
    {
      key: 'previous_cost_usd',
      label: 'Previous',
      align: 'right',
      render: (row) => <span className="text-gray-500">{formatMoney(row.previous_cost_usd)}</span>,
    },
    {
      key: 'current_cost_usd',
      label: 'Current',
      align: 'right',
      render: (row) => <span className="font-bold">{formatMoney(row.current_cost_usd)}</span>,
    },
    {
      key: 'cost_change_usd',
      label: 'Change',
      align: 'right',
      render: (row) => {
        const change = row.cost_change_usd || (row.cost_change ? row.cost_change / 100 : 0);
        const isUp = change > 0;
        return (
          <span
            className={`inline-block px-2 py-0.5 rounded-lg text-xs font-bold ${
              isUp ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
            }`}
          >
            {isUp ? '+' : ''}{formatMoney(change)}
          </span>
        );
      },
    },
    {
      key: 'pct_change',
      label: '%',
      align: 'right',
      render: (row) => {
        const pct = row.pct_change || row.percent_change || 0;
        const isUp = pct > 0;
        return (
          <span className={`text-xs font-bold ${isUp ? 'text-red-600' : 'text-green-600'}`}>
            {formatPercent(pct)}
          </span>
        );
      },
    },
    {
      key: 'last_order_date',
      label: 'Last Ordered',
      render: (row) => <span className="text-xs">{formatDate(row.last_order_date || row.current_date)}</span>,
    },
  ], []);

  return (
    <div>
      <div className="flex gap-3 mb-6 flex-wrap">
        <MetricCard
          label="Price Changes"
          value={items.length.toString()}
          accent="#6366F1"
          icon={'\uD83D\uDCC8'}
        />
        <MetricCard
          label="Price Increases"
          value={increaseCount.toString()}
          accent="#DC2626"
        />
        <MetricCard
          label="Price Decreases"
          value={decreaseCount.toString()}
          accent="#16A34A"
        />
        <MetricCard
          label="Total Increase Impact"
          value={formatMoney(totalIncrease)}
          sub="Across all increases"
          accent="#C62828"
        />
      </div>

      {items.length > 0 ? (
        <DataTable columns={columns} data={items} />
      ) : (
        <EmptyState text="No vendor price changes detected" />
      )}
    </div>
  );
}
